import * as React from "react";
import { cx } from "utils/join-classnames";
import { Button, Spinner } from "ui/components";
import { Selection, Segment, getFileURL } from "core/models";
import { useEditorContext } from "ui/context/EditorContext";
import styles from "./ChunkSelection.module.scss";

interface Props {
    selection: Selection;
    onDeleteHandler(selectionId: string): void;
    isActive: boolean;
    isSelected: boolean;
    onActiveClick(selectionID: string): void;
    onEnableTrimHandler(selection: Selection): void;
    className: string;
    small?: boolean;
}

const ChunkSelection: React.FC<Props> = ({
    selection,
    onDeleteHandler,
    isActive,
    isSelected,
    onActiveClick,
    onEnableTrimHandler,
    className,
    small = false,
}) => {
    const editorContext = useEditorContext();
    const selectionRef = React.useRef<HTMLDivElement>(null);
    const [, setDragging] = React.useState<boolean>(false);
    const onDragging = (e: React.DragEvent<HTMLDivElement>) => {
        setDragging(true);
        e.dataTransfer.setData("card_id", selection.id);
    };

    React.useEffect(() => {
        if (!selectionRef.current) {
            return undefined;
        }
        selectionRef.current.addEventListener("dragend", () =>
            setDragging(false),
        );
        return () => {
            if (selectionRef.current) {
                selectionRef.current.removeEventListener("dragend", () =>
                    setDragging(false),
                );
            }
        };
    }, [selectionRef.current]);

    const handleTrim = () => {
        onActiveClick(selection.id);
        onEnableTrimHandler(selection);
    };
    const isTrimming = selection.mediaFile && selection.mediaFile.isProcessing;
    const isTrimmable = !selection.mediaFile;

    const hasChunk = selection.segments.length > 0;
    let firstChunk: Segment | null = null;
    let lastChunk: Segment | null = null;

    const classes = cx(
        styles.selection,
        small ? styles.selection__smaller : null,
        isActive ? styles.is__active : null,
        isSelected ? styles.is__selected : null,
        isTrimming ? styles.is__trimming : null,
        isTrimmable ? styles.is__trimmable : null,
        !hasChunk ? styles.has__no__chunk : null,
        className,
    );

    if (hasChunk) {
        firstChunk = selection.segments[selection.segments.length - 1];
        [lastChunk] = selection.segments;
    }
    return (
        <div
            className={classes}
            ref={selectionRef}
            id={selection.id}
            onDragStart={onDragging}
        >
            <div className={styles.side}>
                {selection.segments.length > 0 && (
                    <Button
                        type="function"
                        theme="light"
                        icon="trim"
                        tooltip="Trim selection"
                        onClick={handleTrim}
                        disabled={!!(isTrimming || !isTrimmable)}
                    />
                )}
                <Button
                    type="function"
                    theme="light"
                    icon="delete"
                    tooltip="Delete"
                    onClick={() => onDeleteHandler(selection.id)}
                    className={styles.icon}
                />
            </div>
            <div className={styles.content}>
                {isTrimming && isActive && (
                    <Spinner className={styles.spinner} />
                )}
                <button
                    type="button"
                    className={styles.thumbs}
                    onClick={() => onActiveClick(selection.id)}
                >
                    {hasChunk ? (
                        <>
                            <div
                                className={cx(
                                    styles.thumb,
                                    styles.first__thumb,
                                )}
                                style={
                                    firstChunk
                                        ? {
                                              backgroundImage: `url(${getFileURL(
                                                  editorContext.channelId,
                                                  firstChunk.jpgPath,
                                                  firstChunk.token,
                                              )})`,
                                          }
                                        : undefined
                                }
                                title={firstChunk?.channel}
                            />
                            <div
                                className={cx(styles.thumb, styles.last__thumb)}
                                style={
                                    lastChunk
                                        ? {
                                              backgroundImage: `url(${getFileURL(
                                                  editorContext.channelId,
                                                  lastChunk.jpgPath,
                                                  lastChunk.token,
                                              )}`,
                                          }
                                        : undefined
                                }
                                title={lastChunk?.channel}
                            />
                        </>
                    ) : (
                        <span>Empty selection</span>
                    )}
                </button>
            </div>
            <Button type="function" theme="light" icon="reorder" />
        </div>
    );
};

export default ChunkSelection;
